import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { processRefund } from '@/services/paymentService';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface RefundDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactionId: string;
  transactionAmount: number;
  prescriptionStatus?: string;
  onRefundSuccess?: () => void;
}

const RefundDialog: React.FC<RefundDialogProps> = ({
  open,
  onOpenChange,
  transactionId,
  transactionAmount,
  prescriptionStatus,
  onRefundSuccess,
}) => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const isFullRefund = !refundAmount || parseFloat(refundAmount) === transactionAmount;
  const partialRefundAmount = refundAmount ? parseFloat(refundAmount) : transactionAmount;

  const handleClose = () => {
    if (!isProcessing) {
      setRefundAmount('');
      setReason('');
      setError(null);
      onOpenChange(false);
    }
  };

  const validateForm = (): boolean => {
    setError(null);

    if (!reason.trim()) {
      setError('Please provide a reason for the refund');
      return false;
    }

    if (reason.trim().length < 10) {
      setError('Reason must be at least 10 characters long');
      return false;
    }

    if (refundAmount) {
      const amount = parseFloat(refundAmount);
      if (isNaN(amount) || amount <= 0) {
        setError('Refund amount must be a positive number');
        return false;
      }

      if (amount > transactionAmount) {
        setError(`Refund amount cannot exceed transaction amount ($${transactionAmount.toFixed(2)})`);
        return false;
      }
    }

    // Check prescription status
    if (prescriptionStatus && prescriptionStatus !== 'pending' && prescriptionStatus !== 'verified') {
      setError(
        `Refund not allowed. Prescription must be in 'pending' or 'verified' status. Current status: ${prescriptionStatus}`
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const refundData = {
        amount: refundAmount ? parseFloat(refundAmount) : undefined,
        reason: reason.trim(),
      };

      await processRefund(transactionId, refundData);

      toast({
        title: 'Refund Processed Successfully',
        description: `Refund of $${partialRefundAmount.toFixed(2)} has been processed successfully.`,
        variant: 'default',
      });

      handleClose();
      onRefundSuccess?.();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || err.message || 'Failed to process refund. Please try again.';
      setError(errorMessage);
      toast({
        title: 'Refund Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Process Refund</DialogTitle>
          <DialogDescription>
            {isFullRefund
              ? `Process a full refund of $${transactionAmount.toFixed(2)} for this transaction.`
              : `Process a partial refund for this transaction (maximum: $${transactionAmount.toFixed(2)}).`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {prescriptionStatus && prescriptionStatus !== 'pending' && prescriptionStatus !== 'verified' && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Refunds are only allowed for prescriptions in 'pending' or 'verified' status. Current
                status: {prescriptionStatus}
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="refund-amount">Refund Amount (Optional - leave empty for full refund)</Label>
            <Input
              id="refund-amount"
              type="number"
              step="0.01"
              min="0.01"
              max={transactionAmount}
              placeholder={`${transactionAmount.toFixed(2)}`}
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              disabled={isProcessing}
            />
            <p className="text-sm text-muted-foreground">
              Maximum refund: ${transactionAmount.toFixed(2)}. Leave empty for full refund.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="refund-reason">
              Reason for Refund <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="refund-reason"
              placeholder="Please provide a detailed reason for this refund..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isProcessing}
              rows={4}
              minLength={10}
            />
            <p className="text-sm text-muted-foreground">
              Minimum 10 characters required. This information will be logged in the audit trail.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isProcessing || !reason.trim()}>
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              `Process ${isFullRefund ? 'Full' : 'Partial'} Refund`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RefundDialog;

